// How the table talks to the game engine.
// Solo: the engine runs right here in the browser.
// Replay: the same table, fed from a recorded match.
// Online: the same interface, but moves go to the game server, which keeps the real game.
import type { Action, GameView, Seat } from '../../shared/engine.ts';
import { deckToList, type Deck } from '../../shared/deck.ts';
import { Match, type MatchFile } from '../../shared/match.ts';
import { CHAT_KEEP, CHAT_MAX, PROTOCOL, type ChatLine, type ClientMessage, type LiveGame, type OpenGame, type ServerMessage } from '../../shared/online.ts';
import { CARD_DB, DATA_FINGERPRINT, TOKEN_DB } from '../data.ts';
import { CONFIG } from '../../config.ts';
import { session } from '../session.ts';

export interface Connection {
  readonly seat: Seat;
  send(action: Action): void;
  onView(cb: (view: GameView) => void): void;
  onError(cb: (message: string) => void): void;
  close(): void;
}

/** A game played here, on this computer: the solo table, and the ?versus preview with a stand-in opponent. */
export class LocalConnection implements Connection {
  readonly seat: Seat;
  readonly match: Match;
  private started = Date.now();
  private viewCbs: ((v: GameView) => void)[] = [];
  private errorCbs: ((m: string) => void)[] = [];
  private passTimer: ReturnType<typeof setTimeout> | null = null;

  /** Start a new game with a deck, or carry on a saved one. */
  constructor(start: { name: string; deck: Deck }) {
    this.seat = 0;
    {
      const list = deckToList(start.deck);
      // Preview of a two-player table before online play exists: add ?versus to the page address.
      // The other seat gets a copy of your deck and simply passes its turns, so you can see both
      // sides of the table, who is blue and who is red.
      const preview = new URLSearchParams(location.search).has('versus');
      this.match = new Match(CARD_DB, TOKEN_DB, {
        mode: preview ? 'versus' : 'solo',
        names: preview ? [start.name, 'Opponent'] : [start.name],
        decks: preview ? [list, list] : [list],
        seed: Math.floor(Math.random() * 0xffffffff),
        fingerprint: DATA_FINGERPRINT,
      });
    }
    // add ?debug to the page address to reach the game from the browser console
    if (location.search.includes('debug')) Object.assign(window, { game: this.match.game, conn: this, CONFIG });
  }

  private apply(seat: Seat, action: Action) {
    return this.match.apply(seat, action, Date.now() - this.started);
  }

  private emit() {
    const view = this.match.game.view(this.seat);
    for (const cb of this.viewCbs) cb(view);
    this.standIn();
  }

  /** (?versus preview only) the stand-in opponent ends its turn after a moment, and agrees to whatever you propose. */
  private standIn() {
    const s = this.match.game.state;
    if (s.mode !== 'versus' || s.result || this.passTimer) return;
    const other: Seat = this.seat === 0 ? 1 : 0;
    const asked = s.endRequest?.from === this.seat || s.peekRequest?.from === this.seat;
    if (!asked && s.turn === this.seat) return;
    this.passTimer = setTimeout(
      () => {
        this.passTimer = null;
        const now = this.match.game.state;
        if (now.peekRequest?.from === this.seat) this.apply(other, { type: 'peekAnswer', accept: true });
        else if (now.endRequest?.from === this.seat) this.apply(other, { type: 'answerEnd', accept: true });
        else for (let i = 0; i < 4 && this.match.game.state.turn === other; i++) this.apply(other, { type: 'nextPhase' });
        this.emit();
      },
      asked ? 1400 : 2500,
    );
  }

  send(action: Action) {
    const err = this.apply(this.seat, action);
    if (err) for (const cb of this.errorCbs) cb(err);
    this.emit();
  }

  onView(cb: (view: GameView) => void) {
    this.viewCbs.push(cb);
    cb(this.match.game.view(this.seat));
    this.standIn();
  }

  onError(cb: (message: string) => void) {
    this.errorCbs.push(cb);
  }

  close() {
    if (this.passTimer) clearTimeout(this.passTimer);
    this.passTimer = null;
    this.viewCbs = [];
    this.errorCbs = [];
  }
}

export interface ReplayStatus {
  index: number; // how many actions have been played
  total: number;
  time: number; // milliseconds into the match
  length: number;
  playing: boolean;
  speed: number;
  brokeAt: number | null; // the replay cannot go past this action (the card list or rules changed since)
}

/** The gap between two actions as a replay plays them: never a long wait, never a blur. */
const MIN_GAP = 140;
const MAX_GAP = 2600;

/** Plays a recorded match back, one action at a time, seen from one player's seat. */
export class ReplayConnection implements Connection {
  readonly seat: Seat;
  readonly file: MatchFile;
  private match: Match;
  private index = 0;
  private playing = false;
  private speed = 1;
  private brokeAt: number | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private viewCbs: ((v: GameView) => void)[] = [];
  private errorCbs: ((m: string) => void)[] = [];
  private statusCbs: ((s: ReplayStatus, jumped: boolean) => void)[] = [];

  constructor(file: MatchFile, seat: Seat = 0, startAt = 0) {
    this.file = file;
    this.seat = seat < file.names.length ? seat : 0;
    this.match = Match.load(file, CARD_DB, TOKEN_DB, 0).match;
    if (startAt > 0) this.rebuild(startAt);
  }

  get status(): ReplayStatus {
    const total = this.brokeAt ?? this.file.actions.length;
    return {
      index: this.index,
      total,
      time: this.index > 0 ? this.file.actions[this.index - 1].t : 0,
      length: total > 0 ? this.file.actions[total - 1].t : 0,
      playing: this.playing,
      speed: this.speed,
      brokeAt: this.brokeAt,
    };
  }

  private rebuild(upTo: number) {
    const loaded = Match.load(this.file, CARD_DB, TOKEN_DB, upTo);
    this.match = loaded.match;
    this.index = loaded.match.actions.length;
    if (loaded.brokeAt !== null) this.brokeAt = loaded.brokeAt;
  }

  private emit(jumped: boolean) {
    const view = this.match.game.view(this.seat);
    for (const cb of this.statusCbs) cb(this.status, jumped);
    for (const cb of this.viewCbs) cb(view);
  }

  private schedule() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const s = this.status;
    if (!this.playing) return;
    if (s.index >= s.total) {
      this.playing = false;
      this.emit(false);
      return;
    }
    const next = this.file.actions[this.index];
    const gap = Math.max(MIN_GAP, Math.min(MAX_GAP, next.t - s.time)) / this.speed;
    this.timer = setTimeout(() => {
      this.timer = null;
      this.advance();
      this.schedule();
    }, gap);
  }

  /** Plays the next action. */
  private advance() {
    const s = this.status;
    if (s.index >= s.total) return;
    const x = this.file.actions[this.index];
    if (this.match.apply(x.s, x.a, x.t)) {
      this.brokeAt = this.index; // it no longer fits: the replay ends here
      this.playing = false;
    } else this.index++;
    this.emit(false);
  }

  play() {
    if (this.status.index >= this.status.total) this.seek(0);
    this.playing = true;
    this.emit(false);
    this.schedule();
  }
  pause() {
    this.playing = false;
    this.schedule();
    this.emit(false);
  }
  step() {
    this.playing = false;
    this.schedule();
    this.advance();
  }
  setSpeed(speed: number) {
    this.speed = speed;
    this.emit(false);
    this.schedule();
  }
  /** Jumps to just after action number `index` (0 = the opening table). */
  seek(index: number) {
    const target = Math.max(0, Math.min(this.status.total, Math.round(index)));
    if (target !== this.index) this.rebuild(target);
    this.emit(true);
    this.schedule();
  }

  onStatus(cb: (s: ReplayStatus, jumped: boolean) => void) {
    this.statusCbs.push(cb);
    cb(this.status, true);
  }

  /** Nothing can be changed in a replay: the piece just goes back where it was. */
  send() {
    for (const cb of this.errorCbs) cb('This is a replay: you can look around, but nothing on the table can be changed.');
    this.emit(false);
  }

  onView(cb: (view: GameView) => void) {
    this.viewCbs.push(cb);
    cb(this.match.game.view(this.seat));
  }
  onError(cb: (message: string) => void) {
    this.errorCbs.push(cb);
  }
  close() {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.playing = false;
    this.viewCbs = [];
    this.errorCbs = [];
    this.statusCbs = [];
  }
}

// ---------------- online ----------------

/** Where the game server listens: the same address the page came from. */
function serverUrl() {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`;
}

/** The online game this browser is in, kept so a refresh or a dropped connection can take the seat back. */
const ONLINE_KEY = 'arellix.onlineGame';
export interface OnlineSeat {
  code: string;
  token: string;
  names: string[];
}
export function savedOnlineGame(): OnlineSeat | null {
  try {
    const raw = localStorage.getItem(ONLINE_KEY);
    return raw ? (JSON.parse(raw) as OnlineSeat) : null;
  } catch {
    return null;
  }
}
export function forgetOnlineGame() {
  try {
    localStorage.removeItem(ONLINE_KEY);
  } catch {
    /* storage blocked: nothing to forget */
  }
}
function rememberOnlineGame(seat: OnlineSeat) {
  try {
    localStorage.setItem(ONLINE_KEY, JSON.stringify(seat));
  } catch {
    /* storage blocked: a refresh just won't bring the game back */
  }
}

type OnlineStart = { create: { name: string; deck: Deck; open: boolean; teams?: boolean } } | { bot: { name: string; deck: Deck; level: string; tutorial?: boolean } } | { join: { code: string; name: string; deck: Deck } } | { rejoin: OnlineSeat } | { watch: { code: string; side: Seat; names?: string[] } };

/**
 * A game played online: moves go to the game server, which checks them and sends back this player's view.
 * It reconnects by itself if the connection drops, and takes its seat back.
 */
export class OnlineConnection implements Connection {
  seat: Seat = 0;
  code = '';
  names: string[] = [];
  /** the match as the server last sent it: for saving it, and for the replay at the end */
  file: MatchFile | null = null;
  /** who is at the table right now */
  connected: boolean[] = [true, true];
  /** how long the match has run, as of the last view */
  elapsed = 0;
  /** watching someone else's game: nothing can be changed, and there is no seat to take back */
  spectating: boolean;
  /** a code for a game already being played was entered to join it: this browser is watching it instead */
  joinedToWatch = false;
  /** how many people are watching this game */
  watchers = 0;
  /** a 2v2 game (names has four entries; '' = a seat nobody has taken yet) */
  teams = false;
  /** 2v2: who made the game (they start it) */
  host: Seat = 0;
  /** the seats bots play */
  bots: number[] = [];
  /** the Battle Timer: whose turn is timed, and when (by this computer's clock) their time runs out */
  clock: { seat: Seat; endsAt: number; paused?: boolean; left: number } | null = null;
  /** players who have left a game in progress: when (by this computer's clock) they lose if they aren't back */
  awayUntil: (number | null)[] = [];
  /** what has been said at this table (and in its 2v2 lobby), oldest first */
  chat: ChatLine[] = [];
  private chatCbs: ((line: ChatLine | null) => void)[] = [];
  private ws: WebSocket | null = null;
  private token = '';
  private closed = false;
  private retry = 0;
  private lastView: GameView | null = null;
  private viewCbs: ((v: GameView) => void)[] = [];
  private errorCbs: ((m: string) => void)[] = [];
  private presenceCbs: ((connected: boolean[], online: boolean) => void)[] = [];
  private fileCbs: ((f: MatchFile) => void)[] = [];
  private roomCbs: ((started: boolean) => void)[] = [];
  private closedCbs: ((reason: string) => void)[] = [];
  private queue: ClientMessage[] = [];

  /** Make a new game (it waits for someone to join), join one by its code, or take a seat back. */
  private start: OnlineStart;
  constructor(start: OnlineStart) {
    this.start = start;
    this.spectating = 'watch' in start;
    if ('watch' in start) {
      this.code = start.watch.code;
      this.seat = start.watch.side;
      this.names = start.watch.names ?? [];
    }
    if ('rejoin' in start) {
      this.code = start.rejoin.code;
      this.token = start.rejoin.token;
      this.names = start.rejoin.names;
    }
    this.open();
  }

  private hello() {
    // (logged in: the account's sign-in key, so the server knows the name is really yours)
    return { protocol: PROTOCOL, fingerprint: DATA_FINGERPRINT, ...(session.account ? { account: session.account.key } : {}) };
  }

  private open() {
    const ws = new WebSocket(serverUrl());
    this.ws = ws;
    ws.onopen = () => {
      this.retry = 0;
      // the first time: what we came for. After a dropped connection: our seat back.
      if (this.spectating) ws.send(JSON.stringify({ t: 'watch', code: this.code, side: this.seat, ...this.hello() }));
      else if (this.token) ws.send(JSON.stringify({ t: 'rejoin', code: this.code, token: this.token, ...this.hello() }));
      else if ('create' in this.start) ws.send(JSON.stringify({ t: 'create', name: this.start.create.name, deck: deckToList(this.start.create.deck), open: this.start.create.open, teams: !!this.start.create.teams, ...this.hello() }));
      else if ('bot' in this.start) ws.send(JSON.stringify({ t: 'bot', name: this.start.bot.name, deck: deckToList(this.start.bot.deck), level: this.start.bot.level, ...(this.start.bot.tutorial ? { tutorial: true } : {}), ...this.hello() }));
      else if ('join' in this.start) ws.send(JSON.stringify({ t: 'join', code: this.start.join.code, name: this.start.join.name, deck: deckToList(this.start.join.deck), ...this.hello() }));
      for (const m of this.queue.splice(0)) ws.send(JSON.stringify(m));
      for (const cb of this.presenceCbs) cb(this.connected, true);
    };
    ws.onmessage = (e) => this.receive(JSON.parse(String(e.data)) as ServerMessage);
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.closed) return;
      for (const cb of this.presenceCbs) cb(this.connected, false);
      // try again, waiting a little longer each time (but never more than 10 seconds)
      const wait = Math.min(10_000, 800 * 2 ** this.retry++);
      setTimeout(() => !this.closed && this.open(), wait);
    };
  }

  private receive(msg: ServerMessage) {
    switch (msg.t) {
      case 'room':
        this.seat = msg.seat;
        this.code = msg.code;
        this.token = msg.token;
        this.names = msg.names;
        this.teams = !!msg.teams;
        this.host = msg.host ?? 0;
        this.bots = msg.bots ?? [];
        rememberOnlineGame({ code: msg.code, token: msg.token, names: msg.names });
        for (const cb of this.roomCbs) cb(msg.started);
        break;
      case 'watching':
        if (!this.spectating) this.joinedToWatch = true;
        this.spectating = true;
        this.seat = msg.side;
        this.code = msg.code;
        this.names = msg.names;
        for (const cb of this.roomCbs) cb(true);
        break;
      case 'view':
        this.lastView = msg.view;
        this.elapsed = msg.elapsed;
        this.clock = msg.clock ? { seat: msg.clock.seat, endsAt: Date.now() + msg.clock.left, left: msg.clock.left, ...(msg.clock.paused ? { paused: true } : {}) } : null;
        for (const cb of this.viewCbs) cb(msg.view);
        break;
      case 'presence':
        this.connected = msg.connected;
        this.watchers = msg.watchers ?? 0;
        this.awayUntil = (msg.away ?? []).map((ms) => (ms === null || ms === undefined ? null : Date.now() + ms));
        for (const cb of this.presenceCbs) cb(msg.connected, true);
        break;
      case 'chat':
        this.chat = [...this.chat, msg.line].slice(-CHAT_KEEP);
        for (const cb of this.chatCbs) cb(msg.line);
        break;
      case 'chatLog':
        this.chat = msg.lines.slice(-CHAT_KEEP);
        for (const cb of this.chatCbs) cb(null);
        break;
      case 'file':
        this.file = msg.file;
        for (const cb of this.fileCbs.splice(0)) cb(msg.file);
        break;
      case 'refused':
        for (const cb of this.errorCbs) cb(msg.message);
        break;
      case 'error':
        for (const cb of this.errorCbs) cb(msg.message);
        if (msg.fatal) this.end(msg.message);
        break;
      case 'closed':
        this.end(msg.reason);
        break;
    }
  }

  private end(reason: string) {
    if (this.closed) return;
    this.closed = true;
    if (!this.spectating) forgetOnlineGame();
    this.ws?.close();
    for (const cb of this.closedCbs) cb(reason);
  }

  private post(msg: ClientMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
    else this.queue.push(msg);
  }

  send(action: Action) {
    if (this.spectating) {
      for (const cb of this.errorCbs) cb('You are watching this game, so you can’t change anything.');
      return;
    }
    this.post({ t: 'action', action });
  }

  /** 2v2, before the start: move to an empty seat. */
  takeSeat(seat: Seat) {
    this.post({ t: 'seat', seat });
  }
  /** 2v2: the host starts the game once all four seats are taken. */
  startGame() {
    this.post({ t: 'start' });
  }
  /** The host fills an empty seat with a bot (1v1: the game starts at once). */
  fillBot(seat: Seat, level: string) {
    this.post({ t: 'fillBot', seat, level });
  }
  /** 2v2, before the start: the host takes a bot out of its seat. */
  clearBot(seat: Seat) {
    this.post({ t: 'clearBot', seat });
  }

  /** Watching: look from the other player's end of the table. */
  watchFrom(side: Seat) {
    if (!this.spectating) return;
    this.seat = side;
    this.post({ t: 'side', side });
  }

  /** The match so far, from the server: to save it, or to watch it back. */
  requestFile(): Promise<MatchFile> {
    return new Promise((done) => {
      this.fileCbs.push(done);
      this.post({ t: 'file' });
    });
  }

  onView(cb: (view: GameView) => void) {
    this.viewCbs.push(cb);
    if (this.lastView) cb(this.lastView);
  }
  onError(cb: (message: string) => void) {
    this.errorCbs.push(cb);
  }
  /** Who is at the table, and whether this browser is connected to the server at all. */
  onPresence(cb: (connected: boolean[], online: boolean) => void) {
    this.presenceCbs.push(cb);
  }
  /** The server gave us a seat: `started` once both players are in. */
  onRoom(cb: (started: boolean) => void) {
    this.roomCbs.push(cb);
  }
  /** Chat: called with each new line as it is said, or null when the whole log has come (as you sit down). Returns a way to stop. */
  onChat(cb: (line: ChatLine | null) => void) {
    this.chatCbs.push(cb);
    return () => { this.chatCbs = this.chatCbs.filter((c) => c !== cb); };
  }
  /** Say something to everyone at this table. */
  sendChat(text: string) {
    const t = text.trim().slice(0, CHAT_MAX);
    if (t) this.post({ t: 'chat', text: t });
  }

  /** The game is gone (cancelled, refused, or cleared away). */
  onClosed(cb: (reason: string) => void) {
    this.closedCbs.push(cb);
  }

  /** The host gives up on a game nobody has joined yet. */
  cancel() {
    this.post({ t: 'cancel' });
    this.end('cancelled');
  }

  /** Leave the table. The game stays on the server: `forGood` also forgets it here (it's over, or forfeited). */
  close(forGood = false) {
    if (this.closed) return;
    this.closed = true;
    if (forGood && !this.spectating) forgetOnlineGame();
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: 'leave' }));
    this.ws?.close();
    this.viewCbs = [];
    this.errorCbs = [];
    this.presenceCbs = [];
  }
}

/** Watches the list of open games and games being played (for the Single Versus menu). Call the returned function to stop. */
export function watchOpenGames(cb: (games: OpenGame[] | null, live: LiveGame[]) => void): () => void {
  let stopped = false;
  let ws: WebSocket | null = null;
  const open = () => {
    ws = new WebSocket(serverUrl());
    ws.onopen = () => ws?.send(JSON.stringify({ t: 'list' }));
    ws.onmessage = (e) => {
      const msg = JSON.parse(String(e.data)) as ServerMessage;
      if (msg.t === 'lobbies') cb(msg.games, msg.live ?? []);
    };
    ws.onclose = () => {
      if (stopped) return;
      cb(null, []); // can't reach the server right now
      setTimeout(() => !stopped && open(), 4000);
    };
  };
  open();
  return () => {
    stopped = true;
    ws?.close();
  };
}
